import { LoginResponse, StrutturaUser, User } from '../models';

/**
 * List of local storage keys
 */
export enum LocalStorageKeys {
  TOKEN = 'TOKEN',
  REFRESH_TOKEN = 'REFRESH_TOKEN',
  USER_ID = 'USER_ID',
  USER_INFO = 'USER_INFO',
  CREDENTIALS = 'CREDENTIALS',
  EXPIRES_IN = 'EXPIRES_IN',
  LAST_ACTIVITY = 'LAST_ACTIVITY',
  SELECT_APP_ID = 'SELECT_APP_ID',
  SELECTED_STRUTTURA = 'SELECTED_STRUTTURA',
  PERMESSI_LIST = 'PERMESSI_LIST',
  ADMIN_USER = 'ADMIN_USER',
  STRUTTURE_LIST = 'STRUTTURE_LIST',
  NOTIFICATIONS_NUMBER = 'NOTIFICATIONS_NUMBER',
  FIREBASE_TOKEN = 'FIREBASE_TOKEN',
  IS_NOTIFICATION_REDIRECT = 'IS_NOTIFICATION_REDIRECT',
}

// SETTERS //
export const SetToken = (jwt: string) =>
  saveDataToLocalStorage(LocalStorageKeys.TOKEN, jwt);

export const SetCredentials = (credentials: LoginResponse) =>
  saveDataToLocalStorage(LocalStorageKeys.CREDENTIALS, credentials);

export const SetLastActivity = (date: Date) =>
  saveDataToLocalStorage(LocalStorageKeys.LAST_ACTIVITY, date);

export const SetExpires = (date: number) =>
  saveDataToLocalStorage(LocalStorageKeys.EXPIRES_IN, date);

export const SetRefreshToken = (refreshToken: string) =>
  saveDataToLocalStorage(LocalStorageKeys.REFRESH_TOKEN, refreshToken);

export const SetSelectedAppId = (appId: string) =>
  saveDataToLocalStorage(LocalStorageKeys.SELECT_APP_ID, appId);

export const SetUserID = (userId: string) =>
  saveDataToLocalStorage(LocalStorageKeys.USER_ID, userId);

export const SetStruttura = (struttura: StrutturaUser) =>
  saveDataToLocalStorage(LocalStorageKeys.SELECTED_STRUTTURA, struttura);

export const SetAdminUser = (areAdmin: boolean) =>
  saveDataToLocalStorage(LocalStorageKeys.ADMIN_USER, areAdmin);

export const SetPermessiList = (permessiList: string[]) =>
  saveDataToLocalStorage(LocalStorageKeys.PERMESSI_LIST, permessiList);

export const SetStruttureList = (struttureList: StrutturaUser[]) =>
  saveDataToLocalStorage(LocalStorageKeys.STRUTTURE_LIST, struttureList);

export const SetUserInfo = (userInfo: User) =>
  saveDataToLocalStorage(LocalStorageKeys.USER_INFO, userInfo);

export const SetNotificationsNumber = (notificationsNum: number) =>
  saveDataToLocalStorage(LocalStorageKeys.NOTIFICATIONS_NUMBER, notificationsNum);

export const SetFirebaseToken = (token: string) =>
  saveDataToLocalStorage(LocalStorageKeys.FIREBASE_TOKEN, token);

export const SetIsNotificationRedirect = (isNotificationRedirect: boolean) =>
  saveDataToLocalStorage(
    LocalStorageKeys.IS_NOTIFICATION_REDIRECT,
    isNotificationRedirect
  );

// GETTERS //
export const GetToken = () => getDataFromLocalStorage(LocalStorageKeys.TOKEN);

export const GetCredentials = () =>
  getDataFromLocalStorage(LocalStorageKeys.CREDENTIALS);

export const GetLastActivity = (): Date =>
  getDataFromLocalStorage(LocalStorageKeys.LAST_ACTIVITY);

export const GetExpires = (): number =>
  getDataFromLocalStorage(LocalStorageKeys.EXPIRES_IN);

export const GetRefreshToken = (): string =>
  getDataFromLocalStorage(LocalStorageKeys.REFRESH_TOKEN);

export const GetUserId = (): string =>
  getDataFromLocalStorage(LocalStorageKeys.USER_ID);

export const GetSelectedAppId = (): string =>
  getDataFromLocalStorage(LocalStorageKeys.SELECT_APP_ID);

export const GetSelectedStruttura = (): StrutturaUser =>
  getDataFromLocalStorage(LocalStorageKeys.SELECTED_STRUTTURA);

export const GetAdminUser = (): boolean =>
  getDataFromLocalStorage(LocalStorageKeys.ADMIN_USER);

export const GetPermessiList = (): string[] =>
  getDataFromLocalStorage(LocalStorageKeys.PERMESSI_LIST);

export const GetStruttureList = (): StrutturaUser[] =>
  getDataFromLocalStorage(LocalStorageKeys.STRUTTURE_LIST);

export const GetUserInfo = (): User =>
  getDataFromLocalStorage(LocalStorageKeys.USER_INFO);

export const GetIsNotificationRedirect = (): boolean =>
  getDataFromLocalStorage(LocalStorageKeys.IS_NOTIFICATION_REDIRECT);

/**
 * Remove all the data from the local storage.
 */
export const RemoveAuthData = () => {
  for (const key in LocalStorageKeys) {
    removeDataFromLocalStorage(key as LocalStorageKeys);
  }
};

/**
 * Remove only the token/credentials keys, preserving struttura/permessi
 * preferences so a fresh login can restore them via restorePreviousState().
 */
export const RemoveAuthTokenData = () => {
  removeDataFromLocalStorage(LocalStorageKeys.TOKEN);
  removeDataFromLocalStorage(LocalStorageKeys.REFRESH_TOKEN);
  removeDataFromLocalStorage(LocalStorageKeys.CREDENTIALS);
  removeDataFromLocalStorage(LocalStorageKeys.EXPIRES_IN);
  removeDataFromLocalStorage(LocalStorageKeys.USER_ID);
};

/**
 * Save data to the local storage.
 */
const saveDataToLocalStorage = (key: LocalStorageKeys, value: unknown) => {
  localStorage.setItem(key, JSON.stringify(value));
};

/**
 * Get data from the local storage.
 */
const getDataFromLocalStorage = (key: LocalStorageKeys) => {
  const data = localStorage.getItem(key);
  return !!data && data !== 'undefined' ? JSON.parse(data) : null;
};

/**
 * Remove data from the local storage.
 */
export const removeDataFromLocalStorage = (key: LocalStorageKeys) => {
  localStorage.removeItem(key);
};

export const RemoveNotificationRedirect = () =>
  removeDataFromLocalStorage(LocalStorageKeys.IS_NOTIFICATION_REDIRECT);

/**
 * Remove the selected struttura and its permessi, e.g. when a guard finds it no longer valid.
 */
export const RemoveSelectedStrutturaData = () => {
  removeDataFromLocalStorage(LocalStorageKeys.SELECTED_STRUTTURA);
  removeDataFromLocalStorage(LocalStorageKeys.PERMESSI_LIST);
};
